import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { courseEvents, type CourseEvent } from "@/mocks/courseEvents";
import AlertRegistrationModal from "./AlertRegistrationModal";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const EVENT_COLORS: Record<CourseEvent["type"], string> = {
  "zoom-qa": "bg-blue-400",
  "offline-field": "bg-emerald-400",
  deadline: "bg-rose-400",
};

const EVENT_LABELS: Record<CourseEvent["type"], string> = {
  "zoom-qa": "Zoom Q&A",
  "offline-field": "현장임장",
  deadline: "모집마감",
};

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export default function CourseCalendar() {
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    courseTitle?: string;
    courseId?: string;
  }>({ isOpen: false });

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const today = now.getDate();
  const isCurrentMonth =
    currentYear === now.getFullYear() && currentMonth === now.getMonth();

  const eventsByDate = useMemo(() => {
    const map: Record<number, CourseEvent[]> = {};
    courseEvents.forEach((evt) => {
      if (!map[evt.date]) map[evt.date] = [];
      map[evt.date].push(evt);
    });
    return map;
  }, []);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentYear((y) => y - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentYear((y) => y + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleEventClick = (evt: CourseEvent) => {
    if (evt.type === "deadline" && evt.courseTitle) {
      setAlertModal({
        isOpen: true,
        courseTitle: evt.courseTitle,
        courseId: evt.courseId,
      });
    }
  };

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  const calendarRows: (number | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    calendarRows.push(days.slice(i, i + 7));
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
        className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8 mb-10"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <i className="ri-calendar-2-line text-[#C4956A]"></i>
            <h2 className="text-base font-bold text-[#1C1917]">
              이번 달 교육 및 라이브 일정
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="w-8 h-8 rounded-lg border border-[#E8E5E0] flex items-center justify-center hover:border-[#C4956A]/50 hover:text-[#C4956A] transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>
            <span className="text-sm font-semibold text-[#1C1917] min-w-[80px] text-center">
              {currentYear}년 {currentMonth + 1}월
            </span>
            <button
              onClick={nextMonth}
              className="w-8 h-8 rounded-lg border border-[#E8E5E0] flex items-center justify-center hover:border-[#C4956A]/50 hover:text-[#C4956A] transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          {(["zoom-qa", "offline-field", "deadline"] as const).map((type) => (
            <div key={type} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${EVENT_COLORS[type]}`}></span>
              <span className="text-[11px] text-[#5C5650]">{EVENT_LABELS[type]}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="border border-[#E8E5E0] rounded-xl overflow-hidden">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 bg-[#FAFAF8] border-b border-[#E8E5E0]">
            {WEEKDAYS.map((day, idx) => (
              <div
                key={day}
                className={`py-2.5 text-center text-xs font-semibold ${
                  idx === 0 ? "text-rose-400" : idx === 6 ? "text-blue-400" : "text-[#5C5650]"
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${idx}`}
                    className="aspect-[4/3] border-b border-r border-[#F0EDE8] bg-[#FAFAF8]/50"
                  />
                );
              }

              const dayEvents = eventsByDate[day] || [];
              const isToday = isCurrentMonth && day === today;
              const hasDeadlineEvent = dayEvents.some((e) => e.type === "deadline");

              return (
                <div
                  key={`day-${day}`}
                  className={`aspect-[4/3] border-b border-r border-[#F0EDE8] p-1.5 flex flex-col ${
                    hasDeadlineEvent ? "cursor-pointer hover:bg-[#C4956A]/5 transition-colors" : ""
                  }`}
                  onClick={() => {
                    const deadlineEvt = dayEvents.find((e) => e.type === "deadline");
                    if (deadlineEvt) handleEventClick(deadlineEvt);
                  }}
                >
                  <span
                    className={`text-xs font-semibold mb-0.5 self-start ${
                      isToday
                        ? "w-6 h-6 rounded-full bg-[#1C1917] text-white flex items-center justify-center"
                        : "text-[#5C5650]"
                    }`}
                  >
                    {day}
                  </span>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <AnimatePresence mode="wait">
                      {dayEvents.map((evt) => (
                        <motion.div
                          key={evt.id}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          onClick={(e) => {
                            if (evt.type === "deadline") {
                              e.stopPropagation();
                              handleEventClick(evt);
                            }
                          }}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white truncate ${
                            EVENT_COLORS[evt.type]
                          } ${evt.type === "deadline" ? "cursor-pointer" : ""}`}
                          title={`${evt.title}${evt.time ? ` (${evt.time})` : ""}`}
                        >
                          <span className="w-1 h-1 rounded-full bg-white/70 flex-shrink-0 hidden md:block"></span>
                          <span className="truncate">{evt.title}</span>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming events list */}
        <div className="mt-5 pt-4 border-t border-[#F0EDE8]">
          <h3 className="text-xs font-semibold text-[#5C5650] mb-3 flex items-center gap-1.5">
            <i className="ri-notification-3-line text-[#C4956A]"></i>
            다가오는 주요 일정
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {courseEvents
              .filter((evt) => {
                if (!isCurrentMonth) return false;
                return evt.date >= today || evt.type === "zoom-qa";
              })
              .slice(0, 6)
              .map((evt) => (
                <div
                  key={evt.id}
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-[#FAFAF8] border border-[#F0EDE8]"
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${EVENT_COLORS[evt.type]}`}
                  ></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-[#1C1917] truncate">
                      {evt.title}
                    </p>
                    <p className="text-[10px] text-[#8C8780]">
                      {currentMonth + 1}월 {evt.date}일
                      {evt.time && ` · ${evt.time}`}
                    </p>
                  </div>
                  {evt.type === "deadline" && evt.courseTitle && (
                    <button
                      onClick={() =>
                        setAlertModal({
                          isOpen: true,
                          courseTitle: evt.courseTitle,
                          courseId: evt.courseId,
                        })
                      }
                      className="px-2.5 py-1 text-[10px] font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full hover:bg-[#C4956A]/20 transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
                    >
                      알림신청
                    </button>
                  )}
                </div>
              ))}
          </div>
        </div>
      </motion.div>

      <AlertRegistrationModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ isOpen: false })}
        courseTitle={alertModal.courseTitle}
        courseId={alertModal.courseId}
      />
    </>
  );
}